import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { UserActivityAnalytics } from '@/components/analytics/UserActivityAnalytics';
import { ArticleAnalytics } from '@/components/analytics/ArticleAnalytics';
import { VideoAnalytics } from '@/components/analytics/VideoAnalytics';
import { AnalyticsCard } from '@/components/analytics/AnalyticsCard';
import { AnalyticsTabButton } from '@/components/analytics/AnalyticsTabButton';

export const Route = createFileRoute('/admin/analytics')({
  component: AnalyticsPage,
});

type TabValue = 'general' | 'users' | 'articles' | 'videos'

function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState<TabValue>('general');
  const { data: summaryData } = trpc.admin.analytics.getAnalyticsSummary.useQuery();

  return (
    <div className="space-y-6">
      {/* Header with Title and Tab Navigation */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-gray-600 mt-1">View user engagement and content performance metrics</p>
        </div>

        {/* Tab Navigation */}
        <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50">
          <AnalyticsTabButton
            isActive={activeTab === 'general'}
            onClick={() => setActiveTab('general')}
            label="General"
          />
          <AnalyticsTabButton
            isActive={activeTab === 'users'}
            onClick={() => setActiveTab('users')}
            label="User Activity"
          />
          <AnalyticsTabButton
            isActive={activeTab === 'articles'}
            onClick={() => setActiveTab('articles')}
            label="Articles"
          />
          <AnalyticsTabButton
            isActive={activeTab === 'videos'}
            onClick={() => setActiveTab('videos')}
            label="Videos"
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'general' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <AnalyticsCard
              label="Active Accounts"
              value={summaryData?.activeUsers ?? 0}
              tooltip="Current number of registered user accounts in the system"
            />
            <AnalyticsCard
              label="Total App Opens"
              value={summaryData?.totalAppOpens ?? 0}
              tooltip="Total number of times users have opened the app"
            />
            <AnalyticsCard
              label="Total Quizzes Completed"
              value={summaryData?.totalQuizzesCompleted ?? 0}
              tooltip="Total number of quizzes completed by all users"
            />
            <AnalyticsCard
              label="Total Article Views"
              value={summaryData?.totalArticleViews ?? 0}
              tooltip="Total number of article reads across all users"
            />
            <AnalyticsCard
              label="Total Video Views"
              value={summaryData?.totalVideoViews ?? 0}
              tooltip="Total number of times videos have been viewed"
            />
            <AnalyticsCard
              label="Campus Resource Views"
              value={summaryData?.totalCampusResourceViews ?? 0}
              tooltip="Total number of times campus resources have been opened"
            />
          </div>
        )}
        {activeTab === 'users' && <UserActivityAnalytics />}
        {activeTab === 'articles' && <ArticleAnalytics />}
        {activeTab === 'videos' && <VideoAnalytics />}

      </div>
    </div>
  );
}
